import React, {useState} from 'react';
import {getDateAsString} from "../../../../components/Utils/Utils";
import {toKSTISOStringEnd} from "../../../../components/commonmethod/CommonMethod";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {RentalType, UserIdType, UserType} from "../../../../constant/CodeSpecNum";
import {createRentalRequest} from "../../../../api/lifecycle/rental/rentalRequestService";
import {changeRefreshContentStatus} from "../../../../redux/slice/statusSlice";
import Modal from "../../../../components/modal/Modal";
import Selector from "../../../../components/selector/Selector";
import {onCreateIdClickListener} from "../../../../components/commonmethod/simmsDrawerMethod";
import SimmsDatePicker from "../../../../components/datepicker/SimmsDatePicker";
import {dateOnChange} from "../../../../components/commonmethod/simmsDateMethod";
import dayjs from "dayjs";

const RentalAssetModal = ({rentalAsset, refresh}) => {
    const initData = {
        startDate: `${getDateAsString(true)}`, endDate: `${toKSTISOStringEnd(new Date())}`
    }
    const [rentalRequest, setRentalRequest] = useState(initData);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const {t} = useTranslation();
    const dispatch = useDispatch();

    const handleOpenModal = (e) => {
        e.stopPropagation();
        setIsModalOpen(true);
    }

    const handleCloseModal = () => {
        setRentalRequest(initData);
        setIsModalOpen(false);
    }

    const handleCreateRentalRequest = async () => {
        const startDate = new Date(rentalRequest.startDate);
        const endDate = new Date(rentalRequest.endDate);
        if (!rentalRequest.rentUserId) {
            handleActiveError({message: "대여자를 선택해주세요."});
            return;
        }
        if (startDate > endDate) {
            handleActiveError({message: "대여 종료일은 시작일 이후로 선택해주세요."});
            return;
        }
        const rentInfo = {
            ...rentalRequest,
            rentalAssetId: rentalAsset.id,
            rentUserId: rentalRequest.rentUserId,
            rentalType: RentalType.RENT.value
        };
        if (window.confirm(t(`confirmRent`))) {
            await createRentalRequest(rentInfo, () => {
                setIsModalOpen(false);
                handleActiveToast({message: t(`rentDone`)});
                dispatch(changeRefreshContentStatus());
                if (refresh) refresh();
            });
        }
    }

    return (
        <>
            <button
                type={'button'}
                onClick={handleOpenModal}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2563eb')}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    height: '26px',
                    padding: '0 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: 1,
                    color: '#ffffff',
                    backgroundColor: '#2563eb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(37,99,235,0.35)',
                    transition: 'background-color 0.15s ease',
                }}
            >
                <span style={{fontSize: '14px', lineHeight: 1}}>+</span>
                대여 생성
            </button>
            <Modal isOpen={isModalOpen} onClose={() => handleCloseModal()}>
                <div style={{minWidth: '400px', maxWidth: '460px'}}>
                    <div style={{marginBottom: '4px'}}>
                        <h2 style={{fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0}}>대여 신청</h2>
                        <p style={{fontSize: '13px', color: '#6b7280', margin: '6px 0 0'}}>
                            대여 신청 정보를 입력해주세요.
                        </p>
                    </div>

                    <div
                        style={{
                            marginTop: '16px',
                            border: '1px solid #eef0f2',
                            borderRadius: '10px',
                            padding: '2px 14px',
                            backgroundColor: '#fafbfc',
                        }}
                    >
                        <InfoRow label={'대여 자산명'}>
                            {rentalAsset.asset?.assetName}
                        </InfoRow>
                        {rentalAsset.code &&
                            <InfoRow label={'대여 그룹'}>
                                {rentalAsset.code?.name}
                            </InfoRow>
                        }
                        <InfoRow label={'대여 신청자'} required>
                            <Selector
                                title={"대여 신청자"}
                                constraint={{required: true}}
                                onChange={(value) => onCreateIdClickListener(
                                    setRentalRequest, UserType.rentUser, UserIdType.rentUserId, rentalRequest.rentUser, value
                                )}
                                data={rentalRequest.rentUser}
                                uniqueKey={UserType.rentUser}
                                entity={"userAccount"}
                                auth
                            />
                        </InfoRow>
                        <InfoRow label={'대여 시작일'} required>
                            <span style={{color: '#374151'}}>{rentalRequest.startDate}</span>
                        </InfoRow>
                        <InfoRow label={'대여 종료일'} required last>
                            <SimmsDatePicker
                                onChange={(v) => dateOnChange(setRentalRequest, "endDate", v, true)}
                                constraint={{largerThan: dayjs(new Date()), required: true, autoClose: true}}
                                data={rentalRequest.startDate} auth/>
                        </InfoRow>
                    </div>

                    <div className={'flex items-center justify-end gap-2'} style={{marginTop: '20px'}}>
                        <button
                            type={'button'}
                            className={'text-[13px] px-3 py-0.5 text-black rounded-4 border'}
                            onClick={() => handleCloseModal()}
                        >
                            취소
                        </button>
                        <button
                            type={'button'}
                            className={'text-[13px] px-3 py-0.5 border text-white bg-themeDark rounded-4'}
                            onClick={handleCreateRentalRequest}
                        >
                            대여 생성
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

const InfoRow = ({label, required, children, last}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 2px',
            minHeight: '48px',
            borderBottom: last ? 'none' : '1px solid #eef0f2',
        }}
    >
        <div style={{width: '92px', flexShrink: 0, fontSize: '13px', fontWeight: 600, color: '#6b7280'}}>
            {required && <span style={{color: '#ef4444', marginRight: '2px'}}>*</span>}
            {label}
        </div>
        <div style={{flex: 1, minWidth: 0, fontSize: '14px', color: '#111827'}}>
            {children}
        </div>
    </div>
);

export default RentalAssetModal;